// Home - Foto con productos (sections/home-hotspots.liquid)
// Hotspots open a product card on hover (mouse), tap or keyboard focus; the card is kept inside the photo

const HS_EDGE = 12
const HS_CARD_GAP = 32
const HS_CARD_TOP = -44

class HomeHotspots extends HTMLElement {
	connectedCallback() {
		this.box = this.querySelector(".hs__box")
		this.spots = [...this.querySelectorAll("[data-hs-spot]")]
		this.canHover = window.matchMedia("(hover: hover)").matches

		this.spots.forEach(spot => {
			let dot = spot.querySelector("[data-hs-dot]")

			// With a mouse, hover already opened it: a click keeps it open. On touch, a tap toggles.
			dot.addEventListener("click", event => {
				event.stopPropagation()
				if (this.canHover || !spot.classList.contains("is-open")) this.open(spot)
				else this.close(spot)
			})

			if (this.canHover) {
				spot.addEventListener("mouseenter", () => this.open(spot))
				spot.addEventListener("mouseleave", () => this.close(spot))
			}

			// Only keyboard focus opens it: a tap also focuses the button and would fight the click
			spot.addEventListener("focusin", event => {
				if (this.restoringFocus || !event.target.matches(":focus-visible")) return
				this.open(spot)
			})
			spot.addEventListener("focusout", event => {
				if (!spot.contains(event.relatedTarget)) this.close(spot)
			})
		})

		document.addEventListener("click", this.onDocumentClick)
		document.addEventListener("keydown", this.onKeydown)

		if (window.Shopify?.designMode) {
			document.addEventListener("shopify:block:select", this.onBlockSelect)
			document.addEventListener("shopify:block:deselect", this.onBlockDeselect)
		}
	}

	disconnectedCallback() {
		document.removeEventListener("click", this.onDocumentClick)
		document.removeEventListener("keydown", this.onKeydown)
		document.removeEventListener("shopify:block:select", this.onBlockSelect)
		document.removeEventListener("shopify:block:deselect", this.onBlockDeselect)
	}

	open(spot) {
		this.spots.forEach(other => {
			if (other !== spot) this.close(other)
		})
		this.place(spot)
		spot.classList.add("is-open")
		spot.querySelector("[data-hs-dot]").setAttribute("aria-expanded", "true")
	}

	close(spot) {
		spot.classList.remove("is-open")
		spot.querySelector("[data-hs-dot]").setAttribute("aria-expanded", "false")
	}

	// Card goes to the right of the dot, else to the left; when neither fits (narrow screens)
	// it opens below or above the dot. Always clamped inside the photo.
	place(spot) {
		let card = spot.querySelector("[data-hs-card]")
		let box = this.box.getBoundingClientRect()
		let origin = spot.getBoundingClientRect()
		let width = card.offsetWidth
		let height = card.offsetHeight

		let left = HS_CARD_GAP
		let top = HS_CARD_TOP
		let fitsRight = origin.left + HS_CARD_GAP + width <= box.right - HS_EDGE
		let fitsLeft = origin.left - HS_CARD_GAP - width >= box.left + HS_EDGE

		if (!fitsRight && fitsLeft) left = -HS_CARD_GAP - width
		else if (!fitsRight) {
			left = -width / 2
			top = origin.top + HS_CARD_GAP + height <= box.bottom - HS_EDGE ? HS_CARD_GAP : -HS_CARD_GAP - height
		}

		let minLeft = box.left + HS_EDGE - origin.left
		let maxLeft = box.right - HS_EDGE - width - origin.left
		let minTop = box.top + HS_EDGE - origin.top
		let maxTop = box.bottom - HS_EDGE - height - origin.top
		left = Math.min(Math.max(left, minLeft), maxLeft)
		top = Math.min(Math.max(top, minTop), maxTop)

		card.style.setProperty("--hs-card-left", `${left}px`)
		card.style.setProperty("--hs-card-top", `${top}px`)
	}

	onDocumentClick = event => {
		if (this.contains(event.target) && event.target.closest("[data-hs-spot]")) return
		this.spots.forEach(spot => this.close(spot))
	}

	onKeydown = event => {
		if (event.key !== "Escape") return
		let openSpot = this.spots.find(spot => spot.classList.contains("is-open"))
		if (!openSpot) return
		this.close(openSpot)
		this.restoringFocus = true
		openSpot.querySelector("[data-hs-dot]").focus()
		this.restoringFocus = false
	}

	onBlockSelect = event => {
		if (this.spots.includes(event.target)) this.open(event.target)
	}

	onBlockDeselect = event => {
		if (this.spots.includes(event.target)) this.close(event.target)
	}
}

if (!customElements.get("home-hotspots")) customElements.define("home-hotspots", HomeHotspots)
